import { create } from 'zustand';
import { Player } from '../types';
import { playerService } from '../services/api';

interface PlayersState {
  players: Player[];
  loading: boolean;
  lastFetched: number | null;

  fetchPlayers: (force?: boolean) => Promise<void>;
  setPlayers: (players: Player[]) => void;
  updatePlayer: (player: Player) => void;
  addPlayer: (player: Player) => void;
  removePlayer: (playerId: string) => void;
}

export const usePlayersStore = create<PlayersState>()((set, get) => ({
  players: [],
  loading: false,
  lastFetched: null,

  fetchPlayers: async (force?: boolean) => {
    const { lastFetched, loading } = get();
    if (!force && lastFetched && Date.now() - lastFetched < 30000) return;
    if (loading) return;

    set({ loading: true });
    try {
      const data = await playerService.getAllPlayers(!force);
      set({ players: data, lastFetched: Date.now() });
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      set({ loading: false });
    }
  },

  setPlayers: (players) => set({ players, lastFetched: Date.now() }),

  updatePlayer: (player) =>
    set((state) => ({
      players: state.players.map((p) => (p._id === player._id ? player : p)),
    })),

  addPlayer: (player) =>
    set((state) => {
      if (state.players.some((p) => p._id === player._id)) {
        return { players: state.players.map((p) => (p._id === player._id ? player : p)) };
      }
      return { players: [...state.players, player] };
    }),

  removePlayer: (playerId) =>
    set((state) => ({
      players: state.players.filter((p) => p._id !== playerId),
    })),
}));

// --- Memoized derived selectors with closure cache ---

let _lastPlayersForAvailable: Player[] | null = null;
let _cachedAvailable: Player[] = [];
export const selectAvailablePlayers = (state: PlayersState): Player[] => {
  if (state.players === _lastPlayersForAvailable) return _cachedAvailable;
  _lastPlayersForAvailable = state.players;
  _cachedAvailable = state.players.filter((p) => p.status === 'available');
  return _cachedAvailable;
};

let _lastPlayersForUnsold: Player[] | null = null;
let _cachedUnsold: Player[] = [];
export const selectUnsoldPlayers = (state: PlayersState): Player[] => {
  if (state.players === _lastPlayersForUnsold) return _cachedUnsold;
  _lastPlayersForUnsold = state.players;
  _cachedUnsold = state.players.filter((p) => p.status === 'unsold');
  return _cachedUnsold;
};

let _lastPlayersForCategories: Player[] | null = null;
let _cachedCategories: string[] = [];
export const selectCategories = (state: PlayersState): string[] => {
  if (state.players === _lastPlayersForCategories) return _cachedCategories;
  _lastPlayersForCategories = state.players;
  const cats = new Set<string>();
  state.players.forEach((p) => {
    if (p.category) cats.add(p.category);
  });
  _cachedCategories = Array.from(cats).sort();
  return _cachedCategories;
};

let _lastPlayersForAvailableCount: Player[] | null = null;
let _cachedAvailableCount = 0;
export const selectAvailableCount = (state: PlayersState): number => {
  if (state.players === _lastPlayersForAvailableCount) return _cachedAvailableCount;
  _lastPlayersForAvailableCount = state.players;
  _cachedAvailableCount = state.players.filter((p) => p.status === 'available').length;
  return _cachedAvailableCount;
};
